import React, { useState, useEffect } from "react"
import { Svg, G, Line } from "react-native-svg"
import { BFColors } from "@/constants/Colors"

interface DialComponentProps {
  percentage: number
  size?: number
  duration: number // Duration should be in milliseconds
}

const DialComponent: React.FC<DialComponentProps> = ({
  percentage,
  size = 132,
  duration = 1000, // Duration in milliseconds
}) => {
  const strokeWidth = 1.35
  const totalTicks = 60
  const degreesPerTick = 260 / (totalTicks - 1)
  const dialRadius = (size - strokeWidth * 2) / 2

  const [activeTicks, setActiveTicks] = useState(0)

  // Calculate the target number of active ticks based on the percentage
  const targetActiveTicks = Math.ceil((percentage / 100) * totalTicks)

  useEffect(() => {
    if (targetActiveTicks <= 0.0) {
      return
    }

    // Determine the interval between each tick's activation
    const intervalTime = Math.max(1, duration / targetActiveTicks)

    const intervalId = setInterval(() => {
      setActiveTicks((prevActiveTicks) => {
        const nextActiveTicks = prevActiveTicks + 1
        if (nextActiveTicks >= targetActiveTicks) {
          clearInterval(intervalId)
        }
        return nextActiveTicks
      })
    }, intervalTime)

    // Cleanup function to clear the interval when the component unmounts
    return () => {
      clearInterval(intervalId)
    }
  }, [percentage, duration]) // Remove targetActiveTicks from the dependencies array

  // Generate tick elements
  const tickElements = Array.from({ length: totalTicks }, (_, index) => {
    const angle = degreesPerTick * index * (Math.PI / 180)
    const isActive = index < activeTicks
    const tickLength = 9
    const startX =
      dialRadius + (dialRadius - tickLength) * Math.cos(angle) - dialRadius
    const startY =
      dialRadius + (dialRadius - tickLength) * Math.sin(angle) - dialRadius
    const endX = dialRadius + dialRadius * Math.cos(angle) - dialRadius
    const endY = dialRadius + dialRadius * Math.sin(angle) - dialRadius

    return (
      <Line
        key={index}
        x1={startX}
        y1={startY}
        x2={endX}
        y2={endY}
        stroke={isActive ? BFColors.teal : BFColors.lightest}
        strokeWidth={strokeWidth}
      />
    )
  })

  return (
    <Svg
      width={size.toString()}
      height={size.toString()}
      viewBox={`0 0 ${size} ${size}`}
    >
      <G
        transform={`translate(${dialRadius}, ${dialRadius}) rotate(140, 0, 0)`}
      >
        {tickElements}
      </G>
    </Svg>
  )
}

export default DialComponent
